import React from 'react';
import { Form, Input, Select, Row, Col, Button, Space, Card, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { CaseParty, PartyRole } from '../../../../services/case';
import { formRules, validation } from '../../../../utils/validation';

const { Option } = Select;

interface PartyFormProps {
  parties: CaseParty[];
  onChange: (parties: CaseParty[]) => void;
  partyType: 'our_side' | 'other_side';
  minParties?: number;
}

const PartyForm: React.FC<PartyFormProps> = ({
  parties,
  onChange,
  partyType,
  minParties = 0,
}) => {
  const [form] = Form.useForm();

  // Validate party data
  const validateParty = (party: CaseParty): string[] => {
    const errors: string[] = [];
    
    // Only validate party_role for our_side
    if (partyType === 'our_side' && !party.party_role) {
      errors.push('请选择我方立场');
    }
    
    if (!party.name || !party.name.trim()) {
      errors.push('请输入姓名或名称');
    }
    
    if (party.identity_number && !validation.identityNumber(party.identity_number, party.identity_type)) {
      if (party.identity_type === '身份证') {
        errors.push('身份证号格式不正确');
      } else if (party.identity_type === '统一社会信用代码') {
        errors.push('统一社会信用代码格式不正确');
      } else {
        errors.push('身份证号/统一社会信用代码格式不正确');
      }
    }
    
    if (party.phone && !validation.phone(party.phone)) {
      errors.push('联系电话格式不正确');
    }
    
    return errors;
  };

  const handleAdd = () => {
    const newParty: CaseParty = {
      party_type: partyType as any,
      party_role: partyType === 'our_side' ? PartyRole.PLAINTIFF : PartyRole.DEFENDANT,
      name: '',
    };
    onChange([...parties, newParty]);
  };

  const handleRemove = (index: number) => {
    const newParties = parties.filter((_, i) => i !== index);
    onChange(newParties);
  };

  const handleChange = (index: number, field: keyof CaseParty, value: any) => {
    const newParties = [...parties];
    newParties[index] = {
      ...newParties[index],
      [field]: value,
    };
    onChange(newParties);
  };

  // Get validation status for a field
  const getFieldValidation = (party: CaseParty, field: string) => {
    if (field === 'party_role' && partyType === 'our_side' && !party.party_role) {
      return { status: 'error' as const, help: '请选择我方立场' };
    }
    if (field === 'name' && !party.name?.trim()) {
      return { status: 'error' as const, help: '请输入姓名或名称' };
    }
    if (field === 'identity_number' && party.identity_number) {
      if (!validation.identityNumber(party.identity_number, party.identity_type)) {
        return { status: 'error' as const, help: '身份证号/统一社会信用代码格式不正确' };
      }
    }
    if (field === 'phone' && party.phone) {
      if (!validation.phone(party.phone)) {
        return { status: 'error' as const, help: '联系电话格式不正确' };
      }
    }
    return { status: undefined, help: undefined };
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {parties.map((party, index) => (
          <Card
            key={index}
            size="small"
            title={`当事人 ${index + 1}`}
            extra={
              parties.length > minParties && (
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<MinusCircleOutlined />}
                  onClick={() => handleRemove(index)}
                >
                  删除
                </Button>
              )
            }
          >
            <Row gutter={16}>
              {partyType === 'our_side' && (
                <Col span={8}>
                  <Form.Item
                    label="我方立场"
                    required
                    validateStatus={getFieldValidation(party, 'party_role').status}
                    help={getFieldValidation(party, 'party_role').help}
                  >
                    <Select
                      placeholder="请选择立场"
                      value={party.party_role}
                      onChange={(value) => handleChange(index, 'party_role', value)}
                    >
                      <Option value={PartyRole.PLAINTIFF}>原告</Option>
                      <Option value={PartyRole.DEFENDANT}>被告</Option>
                      <Option value={PartyRole.CLIENT}>委托人</Option>
                      <Option value={PartyRole.THIRD_PARTY}>第三人</Option>
                      <Option value={PartyRole.ENFORCEMENT_APPLICANT}>强制执行申请人</Option>
                      <Option value={PartyRole.ENFORCEMENT_RESPONDENT}>被申请执行人</Option>
                      <Option value={PartyRole.SUSPECT_FAMILY}>犯罪嫌疑人家属</Option>
                      <Option value={PartyRole.SUSPECT}>犯罪嫌疑人</Option>
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col span={partyType === 'our_side' ? 8 : 12}>
                <Form.Item
                  label="姓名/名称"
                  required
                  validateStatus={getFieldValidation(party, 'name').status}
                  help={getFieldValidation(party, 'name').help}
                >
                  <Input
                    placeholder="请输入姓名或名称"
                    value={party.name}
                    onChange={(e) => handleChange(index, 'name', e.target.value)}
                    maxLength={100}
                  />
                </Form.Item>
              </Col>
              <Col span={partyType === 'our_side' ? 8 : 12}>
                <Form.Item label="性别">
                  <Select
                    placeholder="请选择性别"
                    value={party.gender}
                    onChange={(value) => handleChange(index, 'gender', value)}
                    allowClear
                  >
                    <Option value="男">男</Option>
                    <Option value="女">女</Option>
                    <Option value="其他">其他</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="身份类型">
                  <Select
                    placeholder="请选择身份类型"
                    value={party.identity_type}
                    onChange={(value) => handleChange(index, 'identity_type', value)}
                    allowClear
                  >
                    <Option value="身份证">身份证</Option>
                    <Option value="统一社会信用代码">统一社会信用代码</Option>
                    <Option value="护照">护照</Option>
                    <Option value="其他">其他</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  label="身份证号/统一社会信用代码"
                  validateStatus={getFieldValidation(party, 'identity_number').status}
                  help={getFieldValidation(party, 'identity_number').help}
                >
                  <Input
                    placeholder="请输入身份证号或统一社会信用代码"
                    value={party.identity_number}
                    onChange={(e) => handleChange(index, 'identity_number', e.target.value)}
                    maxLength={18}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="联系电话"
                  validateStatus={getFieldValidation(party, 'phone').status}
                  help={getFieldValidation(party, 'phone').help}
                >
                  <Input
                    placeholder="请输入联系电话"
                    value={party.phone}
                    onChange={(e) => handleChange(index, 'phone', e.target.value)}
                    maxLength={11}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="法定代表人" tooltip="企业当事人填写">
                  <Input
                    placeholder="请输入法定代表人"
                    value={party.legal_representative}
                    onChange={(e) => handleChange(index, 'legal_representative', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="地址">
                  <Input.TextArea
                    placeholder="请输入地址"
                    value={party.address}
                    onChange={(e) => handleChange(index, 'address', e.target.value)}
                    rows={2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>

      <Button
        type="dashed"
        onClick={handleAdd}
        block
        icon={<PlusOutlined />}
        style={{ marginTop: 16 }}
      >
        添加当事人
      </Button>
    </div>
  );
};

export default PartyForm;
